// components/AutoModSettings.tsx
import { useState, useEffect, useRef } from 'react';
import { PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import {
  updateAutomod,
  createCommentModerationExample,
  updateCommentModerationExample,
  deleteCommentModerationExample,
  CommentModerationExample,
  CommentModerationExampleRequest,
  updateProfileDescription,
} from '../../../api/social';
import { testModComment } from '../../../api/comment';
import { useSocial } from '../../../context/socialProvider';
import CategorySlider from '../../../components/CategorySlider';
import { CategoryScores } from '../../../types/categoryScores';
import SliderDescriptions from '../SliderDescriptions';
import { Save } from './Save';
import { ConfirmationModal } from './ConfirmationModal';
import { HiddenExampleModal } from './HiddenExampleModal';

interface AutoModSettingsProps {
  selectedSocial: any;
}

const AutoModSettings = ({ selectedSocial }: AutoModSettingsProps) => {
  const { currentSocial, refreshSocialInfo } = useSocial();
  const resultRef = useRef<HTMLDivElement>(null);

  // AutoMod Configuration State
  const [autoModActive, setAutoModActive] = useState(false);
  const [initialAutoModActive, setInitialAutoModActive] = useState(false);
  const [autoModInstructions, setAutoModInstructions] = useState('');
  const [initialAutoModInstructions, setInitialAutoModInstructions] =
    useState('');
  const [isSavingAutomod, setIsSavingAutomod] = useState(false);

  // Category Scores State
  const [values, setValues] = useState<CategoryScores>({
    sexual: 1,
    harassment: 1,
    hate: 1,
    illicit: 1,
    'self-harm': 1,
    violence: 1,
  });
  const [initialCategoryScores, setInitialCategoryScores] =
    useState<CategoryScores>({
      sexual: 1,
      harassment: 1,
      hate: 1,
      illicit: 1,
      'self-harm': 1,
      violence: 1,
    });

  // Comment Moderation Examples State
  const [moderationExamples, setModerationExamples] = useState<
    CommentModerationExample[]
  >([]);
  const [showExampleModal, setShowExampleModal] = useState(false);
  const [editingExample, setEditingExample] =
    useState<CommentModerationExample | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<number | null>(
    null
  );
  const [isExampleSaving, setIsExampleSaving] = useState(false);

  // Test Moderation State
  const [modComment, setModComment] = useState('');
  const [isTestingMod, setIsTestingMod] = useState(false);
  const [modCommentResult, setModCommentResult] = useState<{
    evaluation: boolean;
    reasoning: string;
  } | null>(null);

  // Initialize state from selectedSocial
  useEffect(() => {
    if (selectedSocial) {
      setAutoModInstructions(selectedSocial.autoModInstructions || '');
      setInitialAutoModInstructions(selectedSocial.autoModInstructions || '');

      if (selectedSocial.categoryScores) {
        setValues(selectedSocial.categoryScores);
        setInitialCategoryScores(selectedSocial.categoryScores);
      } else {
        // Set default values when no category scores exist
        const defaultScores: CategoryScores = {
          sexual: 1,
          harassment: 1,
          hate: 1,
          illicit: 1,
          'self-harm': 1,
          violence: 1,
        };
        setValues(defaultScores);
        setInitialCategoryScores(defaultScores);
      }

      if (selectedSocial.autoModActive !== undefined) {
        setAutoModActive(selectedSocial.autoModActive);
        setInitialAutoModActive(selectedSocial.autoModActive);
      } else {
        setAutoModActive(false);
        setInitialAutoModActive(false);
      }

      // Initialize comment moderation examples from the social data
      if (selectedSocial?.commentModerationExamples) {
        setModerationExamples(selectedSocial.commentModerationExamples);
      } else {
        setModerationExamples([]);
      }
    }
  }, [selectedSocial]);

  const getSortedCategories = (
    categoryScores: CategoryScores
  ): [string, number][] => {
    return Object.entries(categoryScores).sort(([keyA], [keyB]) =>
      keyA.localeCompare(keyB)
    );
  };

  const handleAutomodSave = async () => {
    if (!selectedSocial) return;
    setIsSavingAutomod(true);
    try {
      await updateAutomod(
        selectedSocial.id,
        autoModActive,
        autoModInstructions,
        values
      );
      setInitialAutoModActive(autoModActive);
      setInitialAutoModInstructions(autoModInstructions);
      setInitialCategoryScores({ ...values });
    } catch (error) {
      console.error('Error saving automod settings:', error);
    } finally {
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setIsSavingAutomod(false);
    }
  };

  const handleToggleAutoMod = async () => {
    setAutoModActive(!autoModActive);
    await updateAutomod(selectedSocial.id, !autoModActive);
    if (currentSocial?.id === selectedSocial.id) {
      await refreshSocialInfo();
    }
  };

  const handleChange = (category: string, newValue: number): void => {
    setValues((prev) => ({
      ...prev,
      [category]: newValue,
    }));
  };

  // Comment Moderation Examples Functions
  const handleAddExample = () => {
    setEditingExample(null);
    setShowExampleModal(true);
  };

  const handleEditExample = (example: CommentModerationExample) => {
    setEditingExample(example);
    setShowExampleModal(true);
  };

  const handleSaveExample = async (exampleData: {
    rule: string;
    reasoning: string;
    comment: string;
  }) => {
    if (!selectedSocial) return;

    setIsExampleSaving(true);
    try {
      if (editingExample) {
        // Update existing example
        const request: CommentModerationExampleRequest = {
          socialId: selectedSocial.id,
          rule: exampleData.rule,
          commentExample: exampleData.comment,
          reasoning: exampleData.reasoning,
        };

        const updatedExample = await updateCommentModerationExample(
          editingExample.id,
          request
        );

        setModerationExamples((prev) =>
          prev.map((ex) => (ex.id === editingExample.id ? updatedExample : ex))
        );
      } else {
        // Create new example
        const request: CommentModerationExampleRequest = {
          socialId: selectedSocial.id,
          rule: exampleData.rule,
          commentExample: exampleData.comment,
          reasoning: exampleData.reasoning,
        };

        const newExample = await createCommentModerationExample(request);
        setModerationExamples((prev) => [...prev, newExample]);
      }
      if (currentSocial?.id === selectedSocial.id) {
        await refreshSocialInfo();
      }
      setShowExampleModal(false);
      setEditingExample(null);
    } catch (error) {
      console.error('Error saving moderation example:', error);
    } finally {
      setIsExampleSaving(false);
    }
  };

  const handleDeleteExample = async (id: number) => {
    if (!selectedSocial) return;

    try {
      await deleteCommentModerationExample(id, selectedSocial.id);
      setModerationExamples((prev) =>
        prev.filter((example) => example.id !== id)
      );
      setShowDeleteConfirm(null);
    } catch (error) {
      console.error('Error deleting moderation example:', error);
    }
  };

  // Test Moderation Functions
  const handleTestModComment = async () => {
    if (selectedSocial && modComment && !isTestingMod) {
      setIsTestingMod(true);
      try {
        const result = await testModComment(modComment, selectedSocial.id);
        setModCommentResult(result.data);
        setTimeout(() => {
          resultRef.current?.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
          });
        }, 100);
      } catch (error) {
        console.error('Error testing mod comment:', error);
      } finally {
        setIsTestingMod(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* AutoMod Master Control */}
      <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="font-semibold text-xl text-white flex items-center">
              AutoMod System
              <span
                className={`ml-3 px-3 py-1 rounded-full text-sm font-bold ${
                  autoModActive
                    ? 'bg-green-500 text-black'
                    : 'bg-gray-500 text-white'
                }`}
              >
                {autoModActive ? 'ENABLED' : 'DISABLED'}
              </span>
            </h2>
            <p className="text-sm text-gray-300 mt-2">
              {autoModActive
                ? 'AutoMod is actively monitoring and moderating comments on this platform'
                : 'AutoMod is disabled. Comments will not be automatically moderated'}
            </p>
          </div>
          <button
            onClick={handleToggleAutoMod}
            className={`relative inline-flex items-center h-8 w-16 rounded-full transition-colors duration-200 focus:outline-none ${
              autoModActive ? 'bg-green-500' : 'bg-gray-400'
            }`}
          >
            <span
              className={`inline-block w-7 h-7 transform bg-white rounded-full shadow transition-transform duration-200 ${
                autoModActive ? 'translate-x-8' : 'translate-x-0.5'
              }`}
            />
          </button>
        </div>
      </div>
      <>
        {/* Test Moderation */}
        <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
          <div className="mb-4">
            <h3 className="font-semibold text-lg text-white mb-2">
              Test Your Settings
            </h3>
            <p className="text-sm text-gray-300">
              Test how your current AutoMod configuration would handle a
              specific comment
            </p>
          </div>

          <div className="flex space-x-3">
            <input
              type="text"
              className="flex-1 bg-black border border-gray-600 rounded p-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet"
              placeholder="Enter a comment to test..."
              value={modComment}
              onChange={(e) => setModComment(e.target.value)}
            />
            <button
              className={`bg-yellow text-black font-semibold px-6 py-3 rounded-xl hover:brightness-90 active:brightness-75 transition-all duration-200 text-sm ${
                !modComment.trim() || isTestingMod
                  ? 'opacity-60 cursor-not-allowed'
                  : ''
              }`}
              onClick={handleTestModComment}
              disabled={isTestingMod || !modComment.trim()}
            >
              {isTestingMod ? (
                <svg
                  className="animate-spin h-5 w-5 text-black"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
              ) : (
                'Test'
              )}
            </button>
          </div>
          {modCommentResult && (
            <div
              ref={resultRef}
              className="mt-4 p-4 bg-black border border-gray-700 rounded text-sm"
            >
              <div className="mb-2">
                <strong>Result:</strong>{' '}
                <span
                  className={`font-bold ${
                    modCommentResult.evaluation
                      ? 'text-red-400'
                      : 'text-green-400'
                  }`}
                >
                  {modCommentResult.evaluation ? 'Moderated' : 'Allowed'}
                </span>
              </div>
              <div>
                <strong>Reason:</strong> {modCommentResult.reasoning}
              </div>
            </div>
          )}
        </div>

        {/* Comment Moderation Examples */}
        <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-semibold text-lg text-white">
                Training Examples
              </h3>
              <p className="text-sm text-gray-300 mt-1">
                Examples help you tailor Automod to block comments that could
                harm your brand or negatively impact conversions.
              </p>
            </div>
            <button
              onClick={handleAddExample}
              className="flex items-center bg-yellow rounded-xl px-3 py-2 text-black font-semibold hover:brightness-90 active:brightness-75 text-sm"
              disabled={isExampleSaving}
            >
              <PlusIcon className="w-4 h-4 mr-1" />
              Add Rule
            </button>
          </div>

          <div className="bg-black border border-gray-600 rounded-lg overflow-hidden">
            <table className="w-full">
              <thead className="bg-black">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">
                    Rule
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">
                    Reasoning
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-gray-200">
                    Comment Example
                  </th>
                  <th className="px-4 py-3 text-center text-sm font-medium text-gray-200">
                    {/* Actions */}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {moderationExamples.map((example) => (
                  <tr key={example.id} className="hover:bg-gray-800/50">
                    <td className="px-4 py-3 text-sm text-white">
                      {example.rule}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {example.reasoning}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-300">
                      {example.commentExample}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex items-center justify-center space-x-2">
                        <button
                          className="text-gray-400 hover:text-white"
                          onClick={() => handleEditExample(example)}
                          disabled={isExampleSaving}
                        >
                          <PencilIcon className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(example.id)}
                          className="text-gray-400 hover:text-red-400"
                          disabled={isExampleSaving}
                        >
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {moderationExamples.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-4 py-8 text-center text-gray-400"
                    >
                      {/* No training examples added yet. Click "Add Example" to
                      help AutoMod learn your preferences. */}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Moderation Instructions & Thresholds */}
        <div className="settings-container bio bg-grey rounded-xl p-6 shadow-lg border border-gray-700">
          <div className="mb-6">
            <h3 className="font-semibold text-lg text-white mb-2">
              Moderation Configuration
            </h3>
            <span className="text-sm text-gray-300 list-disc list-inside space-y-1">
              <strong>Moderation Instructions:</strong> Write specific rules for
              what types of comments should be hidden or allowed.
              <br />
              <strong>Category Thresholds:</strong> Adjust how strict AutoMod
              should be for general categories.
            </span>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Moderation Instructions
              </label>
              <textarea
                className="bg-black border border-gray-600 rounded p-3 w-full text-sm text-white focus:outline-none focus:ring-2 focus:ring-violet"
                style={{ height: '533px' }}
                placeholder="e.g. Hide all comments mentioning counterfeit products or spam links."
                value={autoModInstructions}
                onChange={(e) => setAutoModInstructions(e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-200 mb-2">
                Category Thresholds
              </label>
              <div className="space-y-0">
                {getSortedCategories(values).map(([key, value]) => (
                  <CategorySlider
                    key={key}
                    label={key.charAt(0).toUpperCase() + key.slice(1)}
                    value={value}
                    onChange={(newValue) => handleChange(key, newValue)}
                    descriptions={SliderDescriptions}
                  />
                ))}
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Save
              initialValue={{
                active: initialAutoModActive,
                instructions: initialAutoModInstructions,
                scores: initialCategoryScores,
              }}
              currentValue={{
                active: autoModActive,
                instructions: autoModInstructions,
                scores: values,
              }}
              isLoading={isSavingAutomod}
              onClick={handleAutomodSave}
            />
          </div>
        </div>
      </>

      {/* Modals */}
      <HiddenExampleModal
        isOpen={showExampleModal}
        onClose={() => setShowExampleModal(false)}
        onSave={handleSaveExample}
        example={
          editingExample
            ? {
                rule: editingExample.rule,
                reasoning: editingExample.reasoning,
                comment: editingExample.commentExample,
              }
            : null
        }
      />

      <ConfirmationModal
        isOpen={showDeleteConfirm !== null}
        onClose={() => setShowDeleteConfirm(null)}
        onConfirm={() =>
          showDeleteConfirm && handleDeleteExample(showDeleteConfirm)
        }
        title="Delete Example"
        message="Are you sure you want to delete this moderation example? This action cannot be undone."
      />
    </div>
  );
};

export default AutoModSettings;
